import { CouponArt } from "@/components/marketing/coupon/CouponArt"
import { CouponCodeChip } from "@/components/marketing/coupon/CouponCodeChip"
import { CouponTicket } from "@/components/marketing/coupon/CouponTicket"
import { Button, Collapse, Icon, IconButton, Price, Text } from "@/components/ui"
import { Chevron, Close } from "@/icons"
import type { BagCoupon } from "@/lib/bag-coupon"
import { cn } from "@/lib/cn"
import { useId, useState } from "react"

type CouponGroupCardProps = {
  coupons: readonly BagCoupon[]
  discountCents: number
  onRemove: (code: string) => void
}

/** Several coupons that combine, as one ticket ("2 cupons · −R$ X") that opens to list each code and what it takes off. */
export function CouponGroupCard({ coupons, discountCents, onRemove }: CouponGroupCardProps) {
  const [open, setOpen] = useState(false)
  const listId = useId()
  return (
    <CouponTicket as="li" stub={<CouponArt size={46} />}>
      <div className="py-2 pl-3.5 pr-3">
        <Button
          variant="text"
          tone="neutral"
          size="auto"
          fullWidth
          align="between"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((value) => !value)}
          className="min-h-12"
        >
          <span className="flex min-w-0 flex-col items-start gap-0.5">
            <Text as="span" size="base" weight="semibold" leading="tight" tone="default">
              {coupons.length} cupons
            </Text>
            <Text as="span" size="sm" tone="default">
              <Price cents={discountCents} sign="minus" weight="semibold" tone="success" /> de desconto
            </Text>
          </span>
          <Icon as={Chevron} size="sm" tone="muted" className={cn("transition-transform duration-base ease-standard", open ? "-rotate-90" : "rotate-90")} />
        </Button>
        <Collapse open={open}>
          <ul id={listId} aria-label="Cupons combinados" className="flex flex-col gap-1 pb-1 pr-1">
            {coupons.map((coupon) => (
              <li key={coupon.code} className="flex min-w-0 items-center gap-2">
                <CouponCodeChip code={coupon.code} />
                {coupon.status.kind === "applied" ? (
                  <Price cents={coupon.status.discountCents} sign="minus" size="sm" tone="success" className="ml-auto" />
                ) : null}
                <IconButton icon={Close} label={`Remover cupom ${coupon.code}`} onClick={() => onRemove(coupon.code)} size="icon-md" iconSize="xs" />
              </li>
            ))}
          </ul>
        </Collapse>
      </div>
    </CouponTicket>
  )
}
